import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { Dictionary, groupBy } from 'lodash';

import { SharedService } from '../../../layouts/shared-service';
import {
    accountTypeChangedEvent,
} from '../../../constants/sharedEvents';
import { FbosService } from '../../../services/fbos.service';
// Services
import { GroupsService } from '../../../services/groups.service';
import { DeleteConfirmationComponent } from '../../../shared/components/delete-confirmation/delete-confirmation.component';
import { ManageConfirmationComponent } from '../../../shared/components/manage-confirmation/manage-confirmation.component';
import { NotificationComponent } from '../../../shared/components/notification/notification.component';
import {
    ColumnType,
    TableSettingsComponent,
} from '../../../shared/components/table-settings/table-settings.component';
import { GroupGridState } from '../../../store/reducers/group';
import { FbosDialogNewFboComponent } from '../../fbos/fbos-dialog-new-fbo/fbos-dialog-new-fbo.component';
// Components
import { GroupsDialogNewGroupComponent } from '../groups-dialog-new-group/groups-dialog-new-group.component';
import { GroupsMergeDialogComponent } from '../groups-merge-dialog/groups-merge-dialog.component';
import { AssociationsDialogNewAssociationComponent } from '../../associations/associations-dialog-new-association/associations-dialog-new-association.component';
import { localStorageAccessConstant } from 'src/app/constants/LocalStorageAccessConstant';
import { ManageFboGroupsService } from 'src/app/services/managefbo.service';
import { accountStatusType, AccountType, userAccountType, UserRole } from 'src/app/enums/user-role';
import { GroupFboViewModel } from 'src/app/models/groups';
import {
    animate,
    state,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { GridBase } from 'src/app/services/tables/GridBase';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { GroupFbosGridComponent } from '../group-fbos-grid/group-fbos-grid.component';
import { SnackBarService } from 'src/app/ui-services/snackbar/snackBar.service';
import { FboUiService } from 'src/app/ui-services/fbos/fbo-ui.service';
import { User } from 'src/app/models/user';
import { AppDialogWrapperService } from '../../../ni-components/app-dialog-wrapper/app-dialog-wrapper.service';

const initialColumns: ColumnType[] = [
    {
        id: 'groupName',
        name: 'Group',
        sort: 'asc',
    },
    {
        id: 'expiredFboPricingCount',
        name: 'Pricing Expired',
    },
    {
        id: 'needAttentionCustomers',
        name: 'Need Attentions',
    },
    {
        id: 'lastLogin',
        name: 'Last Login Date',
    },
    {
        id: 'active',
        name: 'Active',
    },
    {
        id: 'users',
        name: 'Users',
    },
    {
        id: 'quotes30Days',
        name: 'Quotes (last 30 days)',
    },
    {
        id: 'orders30Days',
        name: 'Fuel Orders (last 30 days)',
    },
    {
        id: 'expiredFboAccountCount',
        name: 'Accounts Expired',
    }
];

@Component({
    selector: 'app-groups-grid',
    styleUrls: ['./groups-grid.component.scss'],
    templateUrl: './groups-grid.component.html',
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition(
                'expanded <=> collapsed',
                animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
            ),
        ]),
    ],
})
export class GroupsGridComponent extends GridBase implements OnInit {
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(GroupFbosGridComponent) groupFbosGridComponent: GroupFbosGridComponent;


    // Input/Output Bindings
    @Input() groupsFbosData: GroupFboViewModel;
    @Input() groupGridState: GroupGridState;
    @Output() editGroupClicked = new EventEmitter<any>();
    @Output() deleteGroupClicked = new EventEmitter<any>();

    // Members
    pageTitle = 'Groups';
    searchValue = '';

    groupAccountType = accountStatusType;
    fboActiveAccountType = accountStatusType;
    fboAccountType =  userAccountType;

    groupDataSource: any[];
    fboDataSourceDictionary: Dictionary<any>;

    tableLocalStorageKey = 'conductor-group-grid-settings';
    tableLocalStorageFilterKey = 'conductor-group-grid-filter';
    columns: ColumnType[] = [];

    expandedElement: any = null;
    columnsToDisplay: string[] = [];
    dataColumns: string[] = [];

    customColumContent = [
        'expiredFboPricingCount',
        'needAttentionCustomers',
        'active',
        'lastLogin',
        'users',
        'expiredFboAccountCount',
    ];

    dataSource = new MatTableDataSource<any>();
    selection = new SelectionModel<any>(true, []);

    isCustomContent(columnId) {
        return this.customColumContent.includes(columnId);
    }
    constructor(
        private router: Router,
        private groupsService: GroupsService,
        private fbosService: FbosService,
        private sharedService: SharedService,
        private manageFboGroupsService: ManageFboGroupsService,
        private snackBarService: SnackBarService,
        private fboUiService: FboUiService,
        private appDialogWrapperService: AppDialogWrapperService
    ){
        super();
    }
    ngOnInit() {
        this.refreshGrid();
    }

    refreshGrid() {
        this.groupDataSource = this.groupsFbosData.groups;
        this.fboDataSourceDictionary = groupBy(
            this.groupsFbosData.fbos,
            (gf) => {
                return gf.groupId;
            }
        );

        this.columns = this.getClientSavedColumns(
            this.tableLocalStorageKey,
            initialColumns
        );

        if (this.groupGridState.filter) {
            this.applyFilter(this.groupGridState.filter);
        } else if (localStorage.getItem(this.tableLocalStorageFilterKey)) {
            this.applyFilter(
                localStorage.getItem(this.tableLocalStorageFilterKey)
            );
        } else {
            this.applyFilter('');
        }

        this.setTableColumns();

        this.setVirtualScrollVariables(
            this.paginator,
            this.sort,
            this.groupDataSource
        );
    }
    //todo: ot might be moved to base  grid
    private setTableColumns() {
        this.dataColumns = this.getFilteredTableColumns(this.columns);
        this.columnsToDisplay = this.getFilteredTableColumns(this.columns);
        this.columnsToDisplay.push('actions');
        this.columnsToDisplay.unshift('expandIcon', 'select');
    }
    getColumnDisplayString(columnId){
        let column  = this.columns.filter(obj => {
            return obj.id == columnId
          });
        return column[0]?.name ?? '';
    }
    deleteGroup(record) {
        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'Delete Group',
            component: DeleteConfirmationComponent,
            width: '450px',
            data: {
                fullDescription:
                    'You are about to remove this group. This will remove the fbos and all the other data related to the group. Are you sure?',
                item: record,
                includeThis: true,
            },
            hideHeader: true
        });

        dialogWrapper.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.deleteGroupClicked.emit({
                isDeletingGroup: true,
            });

            const deleteIndex = this.groupsFbosData.groups.findIndex(
                (group) => group.oid === record.oid
            );
            this.groupsService.remove(record).subscribe(() => {
                this.groupsFbosData.groups.splice(deleteIndex, 1);
                this.snackBarService.showSuccessSnackBar(record.groupName + ' is deleted');
                this.refreshGrid();
                this.sharedService.emitChange('group deleted');
            });
        });
    }

    deactivateAccount(record) {
        this.fboUiService.deactivateAccount(record);
    }
    activateAccount(record) {
        this.fboUiService.activateAccount(record);
    }

    addNewGroupOrFbo() {
        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'New Group or FBO',
            component: GroupsDialogNewGroupComponent,
            width: '450px',
            data: {
                initialSetupPhase: true,
                oid: 0,
            },
        });

        dialogWrapper.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            if (result.type === 'group') {
                this.editGroupClicked.emit({
                    group: result.data,
                    searchValue: this.searchValue,
                });
            } else {
                this.groupFbosGridComponent.editFBO(result);
            }
        });
    }

    addNewFbo(group: any) {
        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'New FBO',
            component: FbosDialogNewFboComponent,
            width: '450px',
            data: {
                groupId: group.oid,
                initialSetupPhase: true,
                oid: 0,
            },
        });

        dialogWrapper.afterClosed().subscribe((result) => {
            if (result) {
                this.groupFbosGridComponent.editFBO(result);
            }
        });
    }
    manageGroup(group: any) {
        if (!group.active) {
            const dialogWrapper = this.appDialogWrapperService.open({
                title: 'Inactive group',
                component: NotificationComponent,
                width: '450px',
                data: {
                    text: "You can't manage an inactive group!",
                    title: 'Inactive group',
                },
            });
        } else {
            const dialogWrapper = this.appDialogWrapperService.open({
                title: 'Manage Group?',
                component: ManageConfirmationComponent,
                width: '450px',
                hideHeader: true,
                data: {
                    description:
                        'This will temporarily switch your account to a group admin for this group.  Would you like to continue?',
                    group: true,
                    title: 'Manage Group?',
                },
            });
            dialogWrapper.afterClosed().subscribe((result) => {
                if (!result) {
                    return;
                }

                localStorage.setItem(
                    this.tableLocalStorageFilterKey,
                    this.searchValue
                );

                this.sharedService.setCurrentUserPropertyValue<number>(
                    localStorageAccessConstant.managerGroupId,
                    this.sharedService.currentUser.groupId
                );

                this.sharedService.setCurrentUserPropertyValue<number>(
                    localStorageAccessConstant.groupId,
                    group.oid
                );

                this.sharedService.setCurrentUserPropertyValue<UserRole>(
                    localStorageAccessConstant.impersonatedrole,
                    UserRole.GroupAdmin
                );

                this.sharedService.emitChange(accountTypeChangedEvent);

                this.router.navigate(['/default-layout/fbos/']);
            });
        }
    }

    mergeGroups() {
        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'Merge Groups',
            component: GroupsMergeDialogComponent,
            width: '450px',
            data: {
                groups: this.selection.selected,
            },
        });
        dialogWrapper.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            this.selection.clear();

            this.groupsFbosData.groups = this.groupsFbosData.groups.filter(
                (group) =>
                    !result.groups.find(
                        (bg) =>
                            bg.oid !== result.baseGroupId &&
                            bg.oid === group.oid
                    )
            );
            this.groupDataSource = this.groupsFbosData.groups;

            this.dataSource.data = this.groupDataSource;
        });
    }

    addAssociation() {
        const dialogWrapper = this.appDialogWrapperService.open({
            title: 'New Association',
            component: AssociationsDialogNewAssociationComponent,
            width: '450px',
        });
        dialogWrapper.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
        });
    }

    applyFilter(filterValue: string) {
        this.searchValue = filterValue;
        localStorage.setItem(this.tableLocalStorageFilterKey, filterValue);

        const {filteredFbos, filteredGroups} = this.filterAccountTypeAndStatus();

        if (!filterValue) {
            this.groupDataSource = filteredGroups;
            this.dataSource.data = this.groupDataSource;
            return;
        }

        const firstFilteredFbos = filteredFbos.filter(
            (fbo) =>
                this.ifStringContains(fbo.icao, filterValue) ||
                this.ifStringContains(fbo.fbo, filterValue) ||
                fbo.users?.find(
                    (user) =>
                        this.ifStringContains(
                            user.firstName + ' ' + user.lastName,
                            filterValue
                        ) || this.ifStringContains(user.username, filterValue)
                )
        );

        const secondFilteredGroups = filteredGroups.filter(
            (group) =>
                this.ifStringContains(group.groupName, filterValue) ||
                group.users?.find(
                    (user) =>
                        this.ifStringContains(
                            user.firstName + ' ' + user.lastName,
                            filterValue
                        ) || this.ifStringContains(user.username, filterValue)
                ) ||
                firstFilteredFbos.find((fbo) => fbo.groupId === group.oid)
        );

        this.groupDataSource = secondFilteredGroups;

        this.dataSource.data = this.groupDataSource;
    }
    private filterAccountTypeAndStatus() {
        let filteredFbos = this.groupsFbosData.fbos.filter(
            (fbo) =>
                (this.fboActiveAccountType === 'all' ||
                (this.fboActiveAccountType === 'active' && !fbo.accountExpired) ||
                (this.fboActiveAccountType === 'inactive' && fbo.accountExpired))
                &&
                (
                (this.fboAccountType === 'all' ||
                    (this.fboAccountType === 'premium' && !fbo.accountType) ||
                    (this.fboAccountType === 'freemium' && fbo.accountType))
                )
        );
        let filteredGroups = this.groupsFbosData.groups.filter(
            (group) =>
                (this.fboAccountType == "freemium" || this.fboAccountType == "premium") ? ((
                    (this.fboAccountType == "freemium" && !group.hasPremiumFbos) || (this.fboAccountType == "premium" && group.hasPremiumFbos))
                    && group.fboCount > 0 && (this.fboActiveAccountType == "all" || ((this.fboActiveAccountType == "active" && group.hasActiveFbos) || this.fboActiveAccountType == "inactive" && !group.hasActiveFbos))) :
                (this.groupAccountType === 'all' ||
                  (this.groupAccountType === 'active' && group.active) ||
                (this.groupAccountType == 'inactive' && !group.active))
        );
        return {filteredFbos, filteredGroups};
    }
    editGroup(group: any) {
        this.editGroupClicked.emit({
            group,
            searchValue: this.searchValue,
        });
    }

    openSettings() {
        const dialogWrapper = this.appDialogWrapperService.open<ColumnType[]>({
            title: 'Table Settings',
            component: TableSettingsComponent,
            data: [...this.columns]
        });

        dialogWrapper.afterClosed().subscribe((result: ColumnType[]) => {
            if (!result) {
                return; // User cancelled
            }
            this.columns = [...result];
            this.setTableColumns();
            this.refreshSort(this.sort, this.columns);
            this.saveSettings(this.tableLocalStorageKey, this.columns);
        });
    }

    filterChanged() {
        this.applyFilter(this.searchValue);
    }
    isValidPricing(data: any) {
        return data.expiredFboPricingCount === 0 && data.activeFboCount > 0;
    }
    getGroupFreemiumFbos(groupId: number) {
        return this.fboDataSourceDictionary[groupId]?.filter(x => x.accountType != AccountType.Freemium) || [];
    }
    getGroupPremiumFbos(groupId: number) {
        return this.fboDataSourceDictionary[groupId]?.filter(x => x.accountType != AccountType.Premium) || [];
    }
    isNetworkFbo(groupId: number) {
        return this.manageFboGroupsService.getIsNetworkFboSearchByGroup(
            this.groupsFbosData,
            groupId
        );
    }

    //todo: move to utils service
    private ifStringContains(str1: string, str2: string) {
        return (!str1 ? '' : str1)
            .toLowerCase()
            .includes((!str2 ? '' : str2).toLowerCase());
    }

    /** Whether the number of selected elements matches the total number of rows. */
    isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.dataSource.data.length;
        return numSelected === numRows;
    }

    /** Selects all rows if they are not all selected; otherwise clear selection. */
    masterToggle() {
        if (this.isAllSelected()) {
            this.selection.clear();
            return;
        }

        this.selection.select(...this.dataSource.data);
    }
    /** The label for the checkbox on the passed row */
    checkboxLabel(row?: any): string {
        if (!row) {
            return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
        }
        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
            row.position + 1
        }`;
    }
}
